import React from 'react';
import {
  alpha,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  LocalGasStation as FuelIcon,
  Add as AddIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import CircularProgressMui from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import { Controller } from 'react-hook-form';
import { CustomerForm } from 'components/Forms';

export default function FuelClaimDialog({
  open,
  onClose,
  claimForm,
  setClaimForm,
  formErrors,
  setFormErrors,
  ocrResult,
  setOcrResult,
  ocrScanning,
  ocrOdoScanning,
  claimSubmitting,
  uploadProgress,
  needsOdo,
  allVehiclesForClaim,
  projects,
  budgets,
  customers,
  customerType,
  handleScanBill,
  handleScanOdometer,
  handleProjectChange,
  handleBudgetChange,
  handleVehicleChange,
  handleCustomerChange,
  handleExpensesTypeChange,
  handlePaymentDateChange,
  handleOdoChange,
  handleDateChange,
  handleAmountChange,
  customerFilterOptions,
  onSubmit,
  openCustomerModal,
  handleCloseCustomerModal,
  bank,
  setBank,
  customerSubmitting,
  customerControl,
  customerErrors,
  handleCustomerSubmit,
  onSubmitCustomer,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <FuelIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.1} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                สร้างรายการเบิกน้ำมัน
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>กรอกข้อมูลจากบิลน้ำมัน</Typography>
            </Box>
          </Stack>
          {isMobile && (
            <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <DialogContent sx={{ pt: { xs: 2, sm: 2.5 }, px: { xs: 1.5, sm: 3 } }}>
          <Grid container spacing={2.5}>

            {/* ── Section 1: โครงการ / งบ / ประเภท ── */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                disablePortal
                options={projects}
                getOptionLabel={(p) => typeof p === 'string' ? p : `${p.project_number ? `${p.project_number} | ` : ''}${p.customer ? `ลูกค้า: ${p.customer} | ` : ''}${p.name || ''}`}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={claimForm.project}
                onChange={handleProjectChange}
                renderInput={(params) => <TextField {...params} label="โครงการ" size="small" fullWidth />}
                noOptionsText="ไม่พบโครงการ"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                key={claimForm.project?._id || 'no-project'}
                disablePortal
                options={(budgets || []).filter(
                  (b) => !claimForm.project?._id || String(b.project_id) === String(claimForm.project._id)
                )}
                getOptionLabel={(b) => typeof b === 'string' ? b : `${b.prefix || ''}${b.budget_number ? `${b.budget_number} | ` : ''}${b.name || ''}`}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={claimForm.budget}
                onChange={handleBudgetChange}
                disabled={!claimForm.project}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="งบประมาณ"
                    size="small"
                    fullWidth
                    helperText={!claimForm.project ? 'เลือกโครงการก่อน' : ''}
                  />
                )}
                noOptionsText="ไม่มีงบประมาณสำหรับโปรเจคนี้"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="วันที่รับเงิน *"
                type="date"
                size="small"
                fullWidth
                value={claimForm.payment_date}
                onChange={handlePaymentDateChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="ประเภทการเบิก"
                size="small"
                fullWidth
                value={claimForm.expenses_type}
                onChange={handleExpensesTypeChange}
              >
                <MenuItem value="PAY">เบิกเพื่อจ่าย</MenuItem>
                <MenuItem value="REFUND">เบิกคืน</MenuItem>
              </TextField>
            </Grid>

            {/* ── ผู้รับเงิน ── */}
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">ผู้รับเงิน</Typography></Divider>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                freeSolo
                options={customers}
                getOptionLabel={(c) => (typeof c === 'string' ? c : c.name || '')}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={claimForm.customer}
                filterOptions={customerFilterOptions}
                onChange={handleCustomerChange}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return option.isAddNew ? (
                    <li key={option.inputValue || 'add-new'} {...rest}>เพิ่ม "{option.inputValue}"</li>
                  ) : (
                    <li key={option._id} {...rest}>{option.name}</li>
                  );
                }}
                renderInput={(params) => <TextField {...params} label="ผู้รับเงิน *" size="small" fullWidth />}
                noOptionsText="ไม่พบผู้รับเงิน"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="ธนาคาร" size="small" fullWidth value={claimForm.payee.bank} disabled />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="เลขที่บัญชี" size="small" fullWidth value={claimForm.payee.account_number} disabled />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="ชื่อบัญชี" size="small" fullWidth value={claimForm.payee.name} disabled />
            </Grid>

            {/* ── เลือกรถ (user เลือกเองเสมอ ไม่ auto-select จากผลวิเคราะห์ใบเสร็จ) ── */}
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">รถที่ใช้เติมน้ำมัน</Typography></Divider>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                options={allVehiclesForClaim}
                groupBy={(v) => (v.owner_type === 'company' ? 'รถบริษัท' : 'รถของฉัน')}
                getOptionLabel={(v) => `${v.license_plate}${v.brand ? ` — ${v.brand}${v.model_name ? ` ${v.model_name}` : ''}` : ''}`}
                isOptionEqualToValue={(opt, val) => opt.license_plate === val.license_plate}
                value={claimForm.vehicle}
                onChange={handleVehicleChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="ทะเบียนรถ *"
                    size="small"
                    fullWidth
                    error={Boolean(formErrors.vehicle)}
                    helperText={formErrors.vehicle}
                  />
                )}
                noOptionsText="ไม่มีรถในระบบ"
              />
            </Grid>

            {/* ── Section 2: แนบรูป 2 ช่อง ── */}
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">แนบรูปเพื่อให้ AI วิเคราะห์</Typography></Divider>
            </Grid>

            {/* ช่อง 1: ใบเสร็จน้ำมัน */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.8}>
                ใบเสร็จน้ำมัน
              </Typography>
              {claimForm.bill_image ? (
                <Box>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={claimForm.bill_image}
                      alt="bill"
                      sx={{
                        width: '100%',
                        height: 140,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        display: 'block',
                      }}
                    />
                    <Button
                      size="small" color="error" variant="contained"
                      sx={{ position: 'absolute', top: 4, right: 4, minWidth: 0, px: 0.8, py: 0.2, fontSize: 10 }}
                      onClick={() => { setClaimForm((p) => ({ ...p, bill_image: null })); setOcrResult(null); }}
                    >ลบ</Button>
                  </Box>
                  <Button
                    fullWidth size="small" variant="contained" color="secondary"
                    startIcon={ocrScanning ? <CircularProgress size={14} color="inherit" /> : <AiIcon />}
                    onClick={handleScanBill}
                    disabled={ocrScanning}
                    sx={{ mt: 0.8, fontWeight: 700, borderRadius: 1.5, fontSize: 12 }}
                  >
                    {ocrScanning ? 'กำลังวิเคราะห์...' : 'วิเคราะห์ใบเสร็จ'}
                  </Button>
                  {ocrResult && (
                    <Box sx={{ mt: 0.8, p: 1, bgcolor: alpha(theme.palette.success.main, 0.07), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                      <Typography variant="caption" color="success.dark" fontWeight={700} display="block" mb={0.4}>วิเคราะห์สำเร็จ</Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.4}>
                        {ocrResult.license_plate && <Chip size="small" label={ocrResult.license_plate} color="primary" variant="outlined" sx={{ fontSize: 10 }} />}
                        {ocrResult.date && <Chip size="small" label={dayjs(ocrResult.date).format('DD/MM/YY')} variant="outlined" sx={{ fontSize: 10 }} />}
                        {ocrResult.total_amount && <Chip size="small" label={`${ocrResult.total_amount.toLocaleString('th-TH')} ฿`} color="warning" variant="outlined" sx={{ fontSize: 10 }} />}
                        {ocrResult.liters && <Chip size="small" label={`${ocrResult.liters} ล.`} variant="outlined" sx={{ fontSize: 10 }} />}
                      </Stack>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  component="label" htmlFor="bill-image-input"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.6,
                    height: 140,
                    borderRadius: 2,
                    border: `2px dashed ${formErrors.bill_image ? theme.palette.error.main : alpha(theme.palette.warning.main, 0.4)}`,
                    bgcolor: formErrors.bill_image ? alpha(theme.palette.error.main, 0.04) : alpha(theme.palette.warning.main, 0.03),
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: formErrors.bill_image ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.warning.main, 0.07) },
                  }}
                >
                  <FuelIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} align="center">ถ่ายรูปใบเสร็จ</Typography>
                  <Typography variant="caption" color="text.disabled" align="center" sx={{ fontSize: 10 }}>AI อ่าน ทะเบียน / วันที่ / ยอดเงิน</Typography>
                  <input
                    id="bill-image-input"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        setClaimForm((p) => ({ ...p, bill_image: ev.target.result }));
                        setOcrResult(null);
                        setFormErrors((p) => ({ ...p, bill_image: undefined }));
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </Box>
              )}
              {formErrors.bill_image && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{formErrors.bill_image}</Typography>
              )}
            </Grid>

            {/* ช่อง 2: เลขไมล์ */}
            {needsOdo ? (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.8}>
                  มาตรวัดเลขไมล์
                </Typography>
                {claimForm.odo_image ? (
                  <Box>
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={claimForm.odo_image}
                        alt="odometer"
                        sx={{
                          width: '100%',
                          height: 140,
                          objectFit: 'cover',
                          borderRadius: 1.5,
                          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          display: 'block',
                        }}
                      />
                      <Button
                        size="small" color="error" variant="contained"
                        sx={{ position: 'absolute', top: 4, right: 4, minWidth: 0, px: 0.8, py: 0.2, fontSize: 10 }}
                        onClick={() => setClaimForm((p) => ({ ...p, odo_image: null }))}
                      >ลบ</Button>
                    </Box>
                    <Button
                      fullWidth size="small" variant="contained" color="secondary"
                      startIcon={ocrOdoScanning ? <CircularProgress size={14} color="inherit" /> : <AiIcon />}
                      onClick={handleScanOdometer}
                      disabled={ocrOdoScanning}
                      sx={{ mt: 0.8, fontWeight: 700, borderRadius: 1.5, fontSize: 12 }}
                    >
                      {ocrOdoScanning ? 'กำลังวิเคราะห์...' : 'อ่านเลขไมล์'}
                    </Button>
                    {claimForm.odo_in && !ocrOdoScanning && (
                      <Box sx={{ mt: 0.8, p: 1, bgcolor: alpha(theme.palette.success.main, 0.07), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.3)}` }}>
                        <Typography variant="caption" color="success.dark" fontWeight={700} display="block" mb={0.2}>อ่านเลขไมล์สำเร็จ</Typography>
                        <Chip size="small" label={`${Number(claimForm.odo_in).toLocaleString('th-TH')} กม.`} color="info" variant="outlined" sx={{ fontSize: 10 }} />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    component="label" htmlFor="odo-image-input"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.6,
                      height: 140,
                      borderRadius: 2,
                      border: `2px dashed ${formErrors.odo_image ? theme.palette.error.main : alpha(theme.palette.info.main, 0.4)}`,
                      bgcolor: formErrors.odo_image ? alpha(theme.palette.error.main, 0.04) : alpha(theme.palette.info.main, 0.03),
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: formErrors.odo_image ? alpha(theme.palette.error.main, 0.08) : alpha(theme.palette.info.main, 0.07) },
                    }}
                  >
                    <CarIcon sx={{ fontSize: 28, color: 'info.main' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600} align="center">ถ่ายรูปเลขไมล์</Typography>
                    <Typography variant="caption" color="text.disabled" align="center" sx={{ fontSize: 10 }}>AI อ่านตัวเลขจากมาตรวัด</Typography>
                    <input
                      id="odo-image-input"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setClaimForm((p) => ({ ...p, odo_image: ev.target.result }));
                          setFormErrors((p) => ({ ...p, odo_image: undefined }));
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                  </Box>
                )}
                {formErrors.odo_image && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{formErrors.odo_image}</Typography>
                )}
              </Grid>
            ) : (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.8}>
                  มาตรวัดเลขไมล์
                </Typography>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  height: 140,
                  borderRadius: 2,
                  border: `2px dashed ${alpha(theme.palette.info.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.info.main, 0.03),
                  px: 2,
                }}>
                  <Typography variant="caption" color="text.secondary" align="center" fontWeight={600}>
                    ไม่จำเป็นต้องแนบรูปเลขไมล์
                  </Typography>
                  <Typography variant="caption" color="text.disabled" align="center" sx={{ fontSize: 10 }}>
                    รถส่วนตัว / อื่นๆ ไม่ต้องบันทึกมาตรวัด
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* ── Section 2.5: รูปอื่นๆ ── */}
            <Grid item xs={12}>
              <Divider><Typography variant="caption" color="text.secondary">รูปอื่นๆ เพิ่มเติม (ไม่บังคับ)</Typography></Divider>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="flex-start">
                {(claimForm.other_images || []).map((img, idx) => (
                  <Box key={idx} sx={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
                    <Box
                      component="img"
                      src={img}
                      alt={`other-${idx}`}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        display: 'block',
                      }}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        bgcolor: 'background.paper',
                        boxShadow: 1,
                        p: 0.3,
                      }}
                      onClick={() => setClaimForm((p) => ({ ...p, other_images: p.other_images.filter((_, i) => i !== idx) }))}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
                {(claimForm.other_images || []).length < 5 && (
                  <Box
                    component="label"
                    htmlFor="other-image-input"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.4,
                      width: 80,
                      height: 80,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: 10 }}>แนบรูป</Typography>
                    <input
                      id="other-image-input"
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        files.forEach((file) => {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setClaimForm((p) => ({
                              ...p,
                              other_images: [...(p.other_images || []), ev.target.result].slice(0, 5),
                            }));
                          };
                          reader.readAsDataURL(file);
                        });
                        e.target.value = '';
                      }}
                    />
                  </Box>
                )}
              </Stack>
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.6, display: 'block', fontSize: 10 }}>
                แนบรูปเพิ่มเติมได้สูงสุด 5 รูป
              </Typography>
            </Grid>

            {/* ── Section 3: ฟิลด์จากใบเสร็จ (AI กรอกให้ แก้ไขได้) ── */}
            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  {ocrResult ? 'ข้อมูลจากใบเสร็จ (แก้ไขได้)' : 'ข้อมูลจากใบเสร็จ'}
                </Typography>
              </Divider>
            </Grid>

            {needsOdo && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="เลขไมล์ตอนเติมน้ำมัน *"
                  fullWidth size="small"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  value={claimForm.odo_in}
                  error={Boolean(formErrors.odo_in)}
                  helperText={formErrors.odo_in}
                  onChange={(e) => { handleOdoChange(e); setFormErrors((p) => ({ ...p, odo_in: undefined })); }}
                  InputProps={{
                    endAdornment: (
                      <>
                        {ocrResult?.odometer && claimForm.odo_in && (
                          <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />
                        )}
                        <InputAdornment position="end">กม.</InputAdornment>
                      </>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={needsOdo ? 6 : 12}>
              <TextField
                label="วันที่เติมน้ำมัน *"
                type="date"
                fullWidth size="small"
                value={claimForm.date}
                onChange={(e) => { handleDateChange(e); setFormErrors((p) => ({ ...p, date: undefined })); }}
                error={Boolean(formErrors.date)}
                helperText={formErrors.date}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: ocrResult?.date && (
                    <AiIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="ยอดเงินในบิล *"
                fullWidth size="small" type="number"
                value={claimForm.fuel_amount}
                error={Boolean(formErrors.fuel_amount)}
                helperText={formErrors.fuel_amount}
                onChange={(e) => { handleAmountChange(e); setFormErrors((p) => ({ ...p, fuel_amount: undefined })); }}
                InputProps={{
                  endAdornment: (
                    <>
                      {ocrResult?.total_amount && claimForm.fuel_amount && (
                        <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />
                      )}
                      <InputAdornment position="end">บาท</InputAdornment>
                    </>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>

            {/* ── Section 4: ข้อมูลน้ำมันจากใบเสร็จ ── */}
            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">รายละเอียดน้ำมัน (จากใบเสร็จ)</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="จำนวนลิตร *"
                fullWidth size="small" type="number"
                value={claimForm.fuel_liters}
                error={Boolean(formErrors.fuel_liters)}
                helperText={formErrors.fuel_liters}
                onChange={(e) => { setClaimForm((p) => ({ ...p, fuel_liters: e.target.value })); setFormErrors((p) => ({ ...p, fuel_liters: undefined })); }}
                InputProps={{
                  endAdornment: (
                    <>
                      {ocrResult?.liters && claimForm.fuel_liters && <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />}
                      <InputAdornment position="end">ล.</InputAdornment>
                    </>
                  ),
                }}
                placeholder="0.000"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="ราคาต่อลิตร *"
                fullWidth size="small" type="number"
                value={claimForm.fuel_price_per_liter}
                error={Boolean(formErrors.fuel_price_per_liter)}
                helperText={formErrors.fuel_price_per_liter}
                onChange={(e) => { setClaimForm((p) => ({ ...p, fuel_price_per_liter: e.target.value })); setFormErrors((p) => ({ ...p, fuel_price_per_liter: undefined })); }}
                InputProps={{
                  endAdornment: (
                    <>
                      {ocrResult?.price_per_liter && claimForm.fuel_price_per_liter && <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />}
                      <InputAdornment position="end">บ./ล.</InputAdornment>
                    </>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="ราคาก่อน VAT"
                fullWidth size="small" type="number"
                value={claimForm.base_amount}
                onChange={(e) => setClaimForm((p) => ({ ...p, base_amount: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <>
                      {ocrResult?.base_amount && claimForm.base_amount && <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />}
                      <InputAdornment position="end">บาท</InputAdornment>
                    </>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="VAT 7%"
                fullWidth size="small" type="number"
                value={claimForm.vat_amount}
                onChange={(e) => setClaimForm((p) => ({ ...p, vat_amount: e.target.value }))}
                InputProps={{
                  endAdornment: (
                    <>
                      {ocrResult?.vat_amount && claimForm.vat_amount && <AiIcon sx={{ fontSize: 14, color: 'secondary.main', mr: 0.5 }} />}
                      <InputAdornment position="end">บาท</InputAdornment>
                    </>
                  ),
                }}
                placeholder="0.00"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="ชื่อสถานีบริการ *"
                fullWidth size="small"
                value={claimForm.fuel_station}
                error={Boolean(formErrors.fuel_station)}
                helperText={formErrors.fuel_station}
                onChange={(e) => { setClaimForm((p) => ({ ...p, fuel_station: e.target.value })); setFormErrors((p) => ({ ...p, fuel_station: undefined })); }}
                InputProps={{
                  endAdornment: ocrResult?.station_name && claimForm.fuel_station
                    ? <AiIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                    : null,
                }}
                placeholder="เช่น ปตท. / บางจาก / Shell"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="เลขที่ใบกำกับภาษี"
                fullWidth size="small"
                value={claimForm.invoice_number}
                onChange={(e) => setClaimForm((p) => ({ ...p, invoice_number: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: ocrResult?.bill_number && claimForm.invoice_number
                    ? <AiIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                    : null,
                }}
                placeholder="เช่น TIO000016902000088"
              />
            </Grid>

          </Grid>
        </DialogContent>

        {claimSubmitting && (claimForm.bill_image || claimForm.odo_image || claimForm.other_images?.length > 0) && (
          <Box sx={{ px: 3, pb: 1 }}>
            <LinearProgress
              variant={uploadProgress < 100 ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.3 }}>
              {uploadProgress < 100 ? `กำลังอัปโหลดรูป... ${uploadProgress}%` : 'กำลังบันทึกรายการ...'}
            </Typography>
          </Box>
        )}
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} variant="outlined">ยกเลิก</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={claimSubmitting ? <CircularProgress size={16} color="inherit" /> : <FuelIcon />}
            onClick={onSubmit}
            disabled={claimSubmitting}
            sx={{ fontWeight: 700 }}
          >
            {claimSubmitting
              ? (claimForm.bill_image || claimForm.odo_image || claimForm.other_images?.length > 0)
                ? `กำลังอัปโหลด... ${uploadProgress}%`
                : 'กำลังบันทึก...'
              : 'ยืนยันการเบิก'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal: เพิ่มผู้รับเงินใหม่ ── */}
      <Modal
        open={openCustomerModal}
        onClose={handleCloseCustomerModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={openCustomerModal}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'primary.main', color: 'primary.contrastText', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">เพิ่มผู้รับเงินใหม่</Typography>
              </Box>
              <IconButton size="small" onClick={handleCloseCustomerModal} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 3, maxHeight: 'calc(90vh - 64px)', overflowY: 'auto' }}>
              <form onSubmit={handleCustomerSubmit(onSubmitCustomer)}>
                <CustomerForm
                  Controller={Controller}
                  control={customerControl}
                  errors={customerErrors}
                  customerType={customerType}
                  bank={bank}
                  setBank={setBank}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button variant="outlined" onClick={handleCloseCustomerModal} disabled={customerSubmitting}>ยกเลิก</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={customerSubmitting}
                    startIcon={customerSubmitting ? <CircularProgressMui size={16} color="inherit" /> : null}
                  >
                    {customerSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
